﻿import { useEffect, useState } from 'react';
import type { DragEvent } from 'react';
import {
        FiSave,
        FiRepeat,
        FiTrash2,
        FiEdit2
} from 'react-icons/fi';
import { apiCall, ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

type SessionTypeItem = {
        id: string;
        name: string;
        color?: string;
};

type Doctor = {
        id: string;
        name: string;
};

type SessionItem = {
        id: string;
        type: SessionTypeItem;
        startTime: string;
        endTime: string;
};

type WeekDay = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday';
type WeekSessions = Record<WeekDay, SessionItem[]>;

type TemplateItem = {
        name: string;
        sessions: WeekSessions;
};

type SessionModalState = {
        show: boolean;
        day: WeekDay | null;
        session: SessionItem | null;
};

const DRAG_SESSION_TYPE = 'application/te-session-type';
const DRAG_WEEK_SESSION = 'application/te-week-session';
const FALLBACK_SESSION_BG_CLASS = 'bg-blue-200';

const createSessionId = (): string => {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
                return crypto.randomUUID();
        }
        return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const isHexColor = (color?: string): boolean => Boolean(color && color.startsWith('#'));

const normalizeList = <T,>(data: unknown): T[] => {
        if (Array.isArray(data)) return data as T[];
        if (data && typeof data === 'object' && Array.isArray((data as { results?: unknown[] }).results)) {
                return (data as { results: T[] }).results;
        }
        return [];
};

type TimeSelectProps = {
        value: string;
        onChange: (value: string) => void;
};

const TimeSelect = ({ value, onChange }: TimeSelectProps) => {
        return (
                <select
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        className="border rounded px-2 py-1 text-sm"
                >
                        {Array.from({ length: 24 }, (_, i) => {
                                const hour = i.toString().padStart(2, '0');
                                return (
                                        <option key={hour} value={`${hour}:00`}>
                                                {`${hour}:00`}
                                        </option>
                                );
                        })}
                </select>
        );
};

type SessionTypeSelectProps = {
        value: SessionTypeItem | null;
        sessionTypes: SessionTypeItem[];
        onChange: (value: SessionTypeItem) => void;
};

const SessionTypeSelect = ({ value, sessionTypes, onChange }: SessionTypeSelectProps) => {
        return (
                <select
                        value={value?.id || ''}
                        onChange={(e) => {
                                const selected = sessionTypes.find(type => type.id === e.target.value);
                                if (selected) onChange(selected);
                        }}
                        className="border rounded px-2 py-1 text-sm w-full"
                        disabled={sessionTypes.length === 0}
                >
                        {sessionTypes.length === 0 && <option value="">No session types available</option>}
                        {sessionTypes.map(type => (
                                <option key={type.id} value={type.id}>
                                        {type.name}
                                </option>
                        ))}
                </select>
        );
};

type SessionProps = {
        day: WeekDay;
        session: SessionItem;
        onEdit: (session: SessionItem) => void;
        onDelete: (session: SessionItem) => void;
        onDragSession: (event: DragEvent<HTMLDivElement>, day: WeekDay, session: SessionItem) => void;
};

const Session = ({ day, session, onEdit, onDelete, onDragSession }: SessionProps) => {
        return (
                <div
                        draggable
                        onDragStart={(e) => onDragSession(e, day, session)}
                        className={`${isHexColor(session.type.color) ? FALLBACK_SESSION_BG_CLASS : (session.type.color || FALLBACK_SESSION_BG_CLASS)} p-2 rounded flex justify-between items-center group cursor-grab active:cursor-grabbing`}
                        style={isHexColor(session.type.color) ? { backgroundColor: session.type.color } : undefined}
                >
                        <span className="font-medium">{session.type.name}</span>
                        <div className="flex items-center gap-2">
                                <span>{session.startTime} - {session.endTime}</span>
                                <button
                                        onClick={() => onEdit(session)}
                                        className="opacity-0 group-hover:opacity-100 text-gray-600 hover:text-gray-800"
                                >
                                        <FiEdit2 size={16} />
                                </button>
                                <button
                                        onClick={() => onDelete(session)}
                                        className="opacity-0 group-hover:opacity-100 text-gray-600 hover:text-red-600"
                                >
                                        <FiTrash2 size={16} />
                                </button>
                        </div>
                </div>
        );
};

type DayScheduleProps = {
        day: WeekDay;
        sessions: SessionItem[];
        onAddSession: (day: WeekDay) => void;
        onEditSession: (session: SessionItem) => void;
        onDeleteSession: (session: SessionItem) => void;
        onDropToDay: (event: DragEvent<HTMLDivElement>, day: WeekDay) => void;
        onDragSession: (event: DragEvent<HTMLDivElement>, day: WeekDay, session: SessionItem) => void;
        onDragOverDay: (event: DragEvent<HTMLDivElement>, day: WeekDay) => void;
        onDragLeaveDay: () => void;
        isDragOver: boolean;
};

const DaySchedule = ({
        day,
        sessions,
        onAddSession,
        onEditSession,
        onDeleteSession,
        onDropToDay,
        onDragSession,
        onDragOverDay,
        onDragLeaveDay,
        isDragOver,
}: DayScheduleProps) => {
        return (
                <div
                        className={`border rounded p-4 bg-white transition-colors ${isDragOver ? 'border-blue-400 bg-blue-50' : ''}`}
                        onDragOver={(e) => onDragOverDay(e, day)}
                        onDragLeave={onDragLeaveDay}
                        onDrop={(e) => onDropToDay(e, day)}
                >
                        <h3 className="font-semibold mb-2">{day}</h3>
                        <p className="text-xs text-gray-500 mb-2">Drag session types here</p>
                        <div className="space-y-2">
                                {sessions.map((session, index) => (
                                        <Session
                                                key={session.id || index}
                                                day={day}
                                                session={session}
                                                onEdit={onEditSession}
                                                onDelete={onDeleteSession}
                                                onDragSession={onDragSession}
                                        />
                                ))}
                                <button
                                        onClick={() => onAddSession(day)}
                                        className="w-full border-2 border-dashed border-gray-300 rounded p-2 text-gray-500 hover:border-gray-400 hover:text-gray-600"
                                >
                                        + Add Session
                                </button>
                        </div>
                </div>
        );
};

type SessionModalProps = {
        session: SessionItem | null;
        sessionTypes: SessionTypeItem[];
        onSave: (session: SessionItem) => void;
        onClose: () => void;
};

const SessionModal = ({ session, sessionTypes, onSave, onClose }: SessionModalProps) => {
        const [type, setType] = useState<SessionTypeItem | null>(session?.type || sessionTypes[0] || null);
        const [startTime, setStartTime] = useState(session?.startTime || '09:00');
        const [endTime, setEndTime] = useState(session?.endTime || '10:00');

        useEffect(() => {
                if (!type && sessionTypes.length > 0) {
                        setType(sessionTypes[0]);
                }
        }, [type, sessionTypes]);

        const handleSave = () => {
                if (!type) return;
                onSave({ id: session?.id || createSessionId(), type, startTime, endTime });
                onClose();
        };

        return (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center">
                        <div className="bg-white rounded-lg p-6 w-96">
                                <h3 className="text-lg font-semibold mb-4">
                                        {session ? 'Edit Session' : 'Add Session'}
                                </h3>
                                <div className="space-y-4">
                                        <div>
                                                <label className="block text-sm font-medium mb-1">Session Type</label>
                                                <SessionTypeSelect value={type} sessionTypes={sessionTypes} onChange={setType} />
                                        </div>
                                        <div className="flex gap-4">
                                                <div>
                                                        <label className="block text-sm font-medium mb-1">Start Time</label>
                                                        <TimeSelect value={startTime} onChange={setStartTime} />
                                                </div>
                                                <div>
                                                        <label className="block text-sm font-medium mb-1">End Time</label>
                                                        <TimeSelect value={endTime} onChange={setEndTime} />
                                                </div>
                                        </div>
                                        <div className="flex justify-end gap-2">
                                                <button
                                                        onClick={onClose}
                                                        className="px-4 py-2 border rounded hover:bg-gray-50"
                                                >
                                                        Cancel
                                                </button>
                                                <button
                                                        onClick={handleSave}
                                                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                                >
                                                        Save
                                                </button>
                                        </div>
                                </div>
                        </div>
                </div>
        );
};

const StaffRostering = () => {
        const { token } = useAuth();
        const [selectedDoctor, setSelectedDoctor] = useState('');
        const [providers, setProviders] = useState<Doctor[]>([]);
        const [sessionTypes, setSessionTypes] = useState<SessionTypeItem[]>([]);
        const [isLoading, setIsLoading] = useState(true);
        const [loadError, setLoadError] = useState<string | null>(null);
        const [dragOverDay, setDragOverDay] = useState<string | null>(null);
        const [weekSessions, setWeekSessions] = useState<WeekSessions>({
                Monday: [],
                Tuesday: [],
                Wednesday: [],
                Thursday: [],
                Friday: [],
        });
        const [templates, setTemplates] = useState<TemplateItem[]>([]);
        const [sessionModal, setSessionModal] = useState<SessionModalState>({ show: false, day: null, session: null });

        useEffect(() => {
                const loadData = async () => {
                        if (!token) return;
                        setIsLoading(true);
                        setLoadError(null);
                        try {
                                const [sessionTypesResponse, providersResponse] = await Promise.all([
                                        apiCall(`${ENDPOINTS.PROVIDERS.SESSION_TYPES}?page_size=200`, { method: 'GET' }, token),
                                        apiCall(`${ENDPOINTS.PROVIDERS.PROVIDERS}?is_active=true&page_size=200`, { method: 'GET' }, token),
                                ]);

                                const sessionTypesData = normalizeList<Record<string, unknown>>(await sessionTypesResponse.json());
                                const providersData = normalizeList<Record<string, unknown>>(await providersResponse.json());

                                const mappedSessionTypes: SessionTypeItem[] = sessionTypesData
                                        .filter(item => Boolean(item.id) && Boolean(item.name))
                                        .map(item => ({
                                                id: String(item.id),
                                                name: String(item.name),
                                                color: item.color ? String(item.color) : FALLBACK_SESSION_BG_CLASS,
                                        }));

                                const mappedProviders: Doctor[] = providersData
                                        .filter(item => Boolean(item.id))
                                        .map(item => {
                                                const fullName = item.full_name ? String(item.full_name) : `${item.first_name || ''} ${item.last_name || ''}`.trim();
                                                return {
                                                        id: String(item.id),
                                                        name: fullName || 'Unnamed Provider',
                                                };
                                        });

                                setSessionTypes(mappedSessionTypes);
                                setProviders(mappedProviders);
                        } catch (err) {
                                console.error('Error loading provider roster data:', err);
                                setLoadError(err instanceof Error ? err.message : 'Failed to load providers and session types');
                        } finally {
                                setIsLoading(false);
                        }
                };

                loadData();
        }, [token]);

        const handleAddSession = (day: WeekDay) => {
                setSessionModal({ show: true, day, session: null });
        };

        const handleEditSession = (day: WeekDay, session: SessionItem) => {
                setSessionModal({ show: true, day, session });
        };

        const handleDeleteSession = (day: WeekDay, session: SessionItem) => {
                const updatedSessions = {
                        ...weekSessions,
                        [day]: weekSessions[day].filter(s => s !== session)
                };
                setWeekSessions(updatedSessions);
        };

        const handleSaveSession = (day: WeekDay, sessionData: SessionItem) => {
                const updatedSessions = {
                        ...weekSessions,
                        [day]: sessionModal.session
                                ? weekSessions[day].map(s => s === sessionModal.session ? sessionData : s)
                                : [...weekSessions[day], sessionData]
                };
                setWeekSessions(updatedSessions);
        };

        const handleSaveTemplate = () => {
                const templateName = prompt('Enter template name:');
                if (templateName) {
                        setTemplates([...templates, {
                                name: templateName,
                                sessions: weekSessions
                        }]);
                }
        };

        const createSessionFromType = (type: SessionTypeItem): SessionItem => ({
                id: createSessionId(),
                type,
                startTime: '09:00',
                endTime: '10:00'
        });

        const handleDragStartType = (event: DragEvent<HTMLDivElement>, type: SessionTypeItem) => {
                event.dataTransfer.setData(DRAG_SESSION_TYPE, JSON.stringify(type));
                event.dataTransfer.effectAllowed = 'copy';
        };

        const handleDragStartSession = (event: DragEvent<HTMLDivElement>, fromDay: WeekDay, session: SessionItem) => {
                event.dataTransfer.setData(DRAG_WEEK_SESSION, JSON.stringify({ fromDay, sessionId: session.id }));
                event.dataTransfer.effectAllowed = 'move';
        };

        const handleDragOverDay = (event: DragEvent<HTMLDivElement>, day: WeekDay) => {
                event.preventDefault();
                event.dataTransfer.dropEffect = 'copy';
                setDragOverDay(day);
        };

        const handleDragLeaveDay = () => {
                setDragOverDay(null);
        };

        const handleDropToDay = (event: DragEvent<HTMLDivElement>, day: WeekDay) => {
                event.preventDefault();
                setDragOverDay(null);

                const draggedType = event.dataTransfer.getData(DRAG_SESSION_TYPE);
                const draggedSession = event.dataTransfer.getData(DRAG_WEEK_SESSION);

                if (draggedType) {
                        const type = JSON.parse(draggedType);
                        setWeekSessions(prev => ({
                                ...prev,
                                [day]: [...prev[day], createSessionFromType(type)]
                        }));
                        return;
                }

                if (draggedSession) {
                        const { fromDay, sessionId } = JSON.parse(draggedSession) as { fromDay: WeekDay; sessionId: string };
                        if (!fromDay || !sessionId) {
                                return;
                        }

                        if (fromDay === day) {
                                return;
                        }

                        setWeekSessions(prev => {
                                const movingSession = prev[fromDay].find(s => s.id === sessionId);
                                if (!movingSession) return prev;
                                return {
                                        ...prev,
                                        [fromDay]: prev[fromDay].filter(s => s.id !== sessionId),
                                        [day]: [...prev[day], movingSession]
                                };
                        });
                }
        };

        return (
                <div className="max-w-6xl mx-auto p-6">
                        <div className="mb-6 space-y-4">
                                <div className="flex justify-between items-center">
                                        <h1 className="text-2xl font-bold">Provider Roster</h1>
                                        <div className="flex gap-4">
                                                <button
                                                        onClick={handleSaveTemplate}
                                                        className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                                >
                                                        <FiSave size={16} />
                                                        Save Template
                                                </button>
                                        </div>
                                </div>

                                <div className="flex gap-4 items-center">
                                        <select
                                                value={selectedDoctor}
                                                onChange={(e) => setSelectedDoctor(e.target.value)}
                                                className="border rounded px-3 py-2 w-64"
                                        >
                                                <option value="">Select Provider</option>
                                                {providers.map(doctor => (
                                                        <option key={doctor.id} value={doctor.id}>
                                                                {doctor.name}
                                                        </option>
                                                ))}
                                        </select>

                                        <button className="flex items-center gap-2 px-4 py-2 border rounded hover:bg-gray-50">
                                                <FiRepeat size={16} />
                                                Set Repeat Pattern
                                        </button>
                                </div>
                        </div>

                        {loadError && (
                                <div className="mb-4 text-sm text-error">{loadError}</div>
                        )}

                        {isLoading ? (
                                <div className="flex items-center justify-center py-12">
                                        <span className="loading loading-spinner loading-lg"></span>
                                </div>
                        ) : (
                        <div className="grid grid-cols-6 gap-4">
                                <div className="col-span-1">
                                        <div className="border rounded p-4 bg-white">
                                                <h3 className="font-semibold mb-2">Session Types</h3>
                                                <div className="space-y-2">
                                                        {sessionTypes.map(type => (
                                                                <div
                                                                        key={type.id}
                                                                        draggable
                                                                        onDragStart={(e) => handleDragStartType(e, type)}
                                                                        className={`${isHexColor(type.color) ? FALLBACK_SESSION_BG_CLASS : (type.color || FALLBACK_SESSION_BG_CLASS)} p-2 rounded cursor-grab active:cursor-grabbing`}
                                                                        style={isHexColor(type.color) ? { backgroundColor: type.color } : undefined}
                                                                >
                                                                        {type.name}
                                                                </div>
                                                        ))}
                                                </div>
                                        </div>
                                </div>

                                <div className="col-span-5">
                                        <div className="grid grid-cols-5 gap-4">
                                                {(Object.entries(weekSessions) as [WeekDay, SessionItem[]][]).map(([day, sessions]) => (
                                                        <DaySchedule
                                                                key={day}
                                                                day={day}
                                                                sessions={sessions}
                                                                onAddSession={handleAddSession}
                                                                onEditSession={(session) => handleEditSession(day, session)}
                                                                onDeleteSession={(session) => handleDeleteSession(day, session)}
                                                                onDropToDay={handleDropToDay}
                                                                onDragSession={handleDragStartSession}
                                                                onDragOverDay={handleDragOverDay}
                                                                onDragLeaveDay={handleDragLeaveDay}
                                                                isDragOver={dragOverDay === day}
                                                        />
                                                ))}
                                        </div>
                                </div>
                        </div>
                        )}

                        {sessionModal.show && (
                                <SessionModal
                                        session={sessionModal.session}
                                        sessionTypes={sessionTypes}
                                        onSave={(sessionData) => {
                                                if (!sessionModal.day) return;
                                                handleSaveSession(sessionModal.day, sessionData);
                                        }}
                                        onClose={() => setSessionModal({ show: false, day: null, session: null })}
                                />
                        )}
                </div>
        );
};

export default StaffRostering;